import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { useAuthStore } from "@/store";
import {
  TreeDeciduous, LayoutDashboard, FileSpreadsheet,
  Settings, LogOut, ChevronLeft, ChevronRight, User, Calendar, Building2, HelpCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState } from "react";
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from "@/components/ui/tooltip";
import { t } from "@/i18n/bg";

export function AppLayout() {
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const isAdmin = useAuthStore((s) => s.isAdmin);
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState(false);

  const handleLogout = () => { logout(); navigate("/login"); };

  const navItems = [
    { to: "/dashboard", icon: LayoutDashboard, label: t("dashboard") },
    ...(isAdmin() ? [{ to: "/templates", icon: FileSpreadsheet, label: t("templates") }] : []),
    ...(isAdmin() ? [{ to: "/periods", icon: Calendar, label: t("periods") }] : []),
    ...(isAdmin() ? [{ to: "/admin", icon: Settings, label: t("admin") }] : []),
    { to: "/unit-settings", icon: Building2, label: isAdmin() ? t("myProfile") : t("unitSettings") },
    { to: "/instructions", icon: HelpCircle, label: t("instructions") },
  ];

  return (
    <div className="flex h-screen overflow-hidden">
      <aside data-testid="app-sidebar"
        className={`${collapsed ? 'w-[68px]' : 'w-[250px]'} bg-[#052e16] flex flex-col transition-all duration-200 shrink-0`}>
        <div className={`flex items-center gap-3 px-4 h-16 border-b border-white/15 ${collapsed ? 'justify-center' : ''}`}>
          <div className="w-9 h-9 rounded-lg bg-white/15 flex items-center justify-center shrink-0">
            <TreeDeciduous className="w-5 h-5 text-white" />
          </div>
          {!collapsed && (
            <div>
              <p className="text-sm font-bold text-white tracking-tight">{t("appName")}</p>
              <p className="text-[10px] text-white/60 uppercase tracking-wider">{t("appSubtitle")}</p>
            </div>
          )}
        </div>
        <nav className="flex-1 py-4 px-2 space-y-1">
          <TooltipProvider delayDuration={0}>
            {navItems.map((item) => (
              <Tooltip key={item.to}>
                <TooltipTrigger asChild>
                  <NavLink to={item.to} data-testid={`nav-${item.to.replace('/','')||'dashboard'}`}
                    className={({ isActive }) =>
                      `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-colors ${
                        isActive ? 'bg-white/20 text-white font-semibold' : 'text-white hover:bg-white/10 font-medium'
                      } ${collapsed ? 'justify-center' : ''}`
                    }>
                    <item.icon className="w-[18px] h-[18px] shrink-0 text-white" />
                    {!collapsed && item.label}
                  </NavLink>
                </TooltipTrigger>
                {collapsed && <TooltipContent side="right" className="text-xs">{item.label}</TooltipContent>}
              </Tooltip>
            ))}
          </TooltipProvider>
        </nav>
        <div className="border-t border-white/10 p-3 space-y-2">
          {!collapsed && (
            <div className="flex items-center gap-2 px-2 py-1">
              <div className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
                <User className="w-4 h-4 text-white" />
              </div>
              <div className="overflow-hidden">
                <p className="text-xs font-medium text-white truncate">{user?.name || user?.email}</p>
                <p className="text-[10px] text-white/70 truncate">
                  {user?.role === 'CENTRAL_ADMIN' ? t("centralAdmin") : user?.unit_name || t("unitUser")}
                </p>
              </div>
            </div>
          )}
          <div className={`flex ${collapsed ? 'flex-col items-center' : 'items-center justify-between'} gap-1`}>
            <Button data-testid="logout-button" variant="ghost" size="sm" onClick={handleLogout}
              className="text-white/70 hover:text-white hover:bg-white/10 text-xs h-8">
              <LogOut className="w-4 h-4 text-white" />
              {!collapsed && <span className="ml-1">{t("logout")}</span>}
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setCollapsed(!collapsed)}
              className="text-white/50 hover:text-white hover:bg-white/10 h-8 w-8 p-0">
              {collapsed ? <ChevronRight className="w-4 h-4 text-white" /> : <ChevronLeft className="w-4 h-4 text-white" />}
            </Button>
          </div>
        </div>
      </aside>
      <main className="flex-1 overflow-auto bg-[#f8fafc]">
        <Outlet />
      </main>
    </div>
  );
}
